/**
 * @file voipRecordingPolicy.js
 * @description
 * Pure (React-free, IO-free) rules of the on-device call recording feature:
 * the stored preferences, which calls are recorded automatically, and the
 * retry schedule of the upload queue. Kept separate from the service so the
 * policy is trivially unit-testable.
 *
 * Pure JavaScript (ESM).
 */

/** When a call is recorded without the user pressing REC. */
export const AUTO_MODES = Object.freeze(['manual', 'incoming', 'outgoing', 'all']);

export const DEFAULT_RECORDING_PREFS = Object.freeze({enabled: false, auto: 'manual'});

/** Upload retry policy: delay after the Nth failed attempt, then give up. */
export const UPLOAD_RETRY_DELAYS_MS = Object.freeze([30000, 120000, 600000, 1800000, 3600000, 3 * 3600000, 6 * 3600000]);
export const UPLOAD_MAX_AGE_MS = 7 * 24 * 3600000;

/** HTTP statuses that will never succeed on retry: the queued file is dropped. */
export const UPLOAD_PERMANENT_FAILURES = Object.freeze([400, 403, 404, 409, 413, 415, 422]);

/**
 * Sanitise stored / incoming preferences.
 * @param {*} raw Anything (parsed JSON, undefined, …).
 * @returns {{enabled: boolean, auto: ('manual'|'incoming'|'outgoing'|'all')}}
 */
export function normalizeRecordingPrefs(raw) {
    const value = raw && typeof raw === 'object' ? raw : {};

    return {
        enabled: value.enabled === true,
        auto: AUTO_MODES.includes(value.auto) ? value.auto : DEFAULT_RECORDING_PREFS.auto,
    };
}

/**
 * Whether a call starts recording on its own.
 * @param {{enabled: boolean, auto: string}} prefs Normalised preferences.
 * @param {string|null} direction `incoming` | `outgoing`.
 * @returns {boolean}
 */
export function shouldAutoRecord(prefs, direction) {
    if (!prefs || prefs.enabled !== true) {
        return false;
    }
    if (prefs.auto === 'all') {
        return direction === 'incoming' || direction === 'outgoing';
    }

    return (prefs.auto === 'incoming' || prefs.auto === 'outgoing') && prefs.auto === direction;
}

/**
 * Next state of the per-call REC toggle.
 * `off` (nothing captured yet) → `recording` → `paused` → `recording` …
 * @param {'off'|'recording'|'paused'} state Current state.
 * @returns {'recording'|'paused'}
 */
export function nextRecordingState(state) {
    return state === 'recording' ? 'paused' : 'recording';
}

/**
 * Milliseconds to wait before the next upload attempt, or null to give up.
 * @param {number} attempts Failed attempts so far (>= 1).
 * @returns {(number|null)}
 */
export function uploadRetryDelayMs(attempts) {
    const index = Math.max(0, Number(attempts) - 1);

    return index < UPLOAD_RETRY_DELAYS_MS.length ? UPLOAD_RETRY_DELAYS_MS[index] : null;
}

/**
 * Whether a queued upload is due now.
 * @param {{nextAttemptAt?: number}} entry Queue entry.
 * @param {number} [now] Timestamp (ms).
 * @returns {boolean}
 */
export function isUploadDue(entry, now = Date.now()) {
    return !entry || !entry.nextAttemptAt || entry.nextAttemptAt <= now;
}

/**
 * Whether a queued upload is too old to keep.
 * @param {{createdAt?: number}} entry Queue entry.
 * @param {number} [now] Timestamp (ms).
 * @returns {boolean}
 */
export function isUploadExpired(entry, now = Date.now()) {
    return Boolean(entry && entry.createdAt && now - entry.createdAt > UPLOAD_MAX_AGE_MS);
}

/**
 * Multipart text fields of the upload (empty values omitted).
 * @param {object} meta Recording metadata.
 * @returns {Object<string, string>}
 */
export function buildUploadFields(meta = {}) {
    const fields = {
        clientRecordingId: meta.clientRecordingId,
        direction: meta.direction,
        callId: meta.callId,
        sessionId: meta.sessionId,
        number: meta.number,
        startedAt: meta.startedAt,
        durationSeconds: Number.isFinite(meta.durationSeconds) ? Math.round(meta.durationSeconds) : undefined,
        recordedSeconds: Number.isFinite(meta.recordedSeconds) ? Math.round(meta.recordedSeconds) : undefined,
    };

    return Object.fromEntries(
        Object.entries(fields)
            .filter(([, value]) => value !== undefined && value !== null && value !== '')
            .map(([key, value]) => [key, String(value)]),
    );
}
